import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { openSheinPathInNewTab, SHEIN_NOTICE_HELP_PATH } from '@/shein-platform/constants/sheinRoutes';
import {
  closeSheinNotice,
  getSheinNoticeAlertItems,
  getSheinNoticeChannel,
  getSheinNoticeLockedChannel,
  subscribeSheinNoticeAlerts,
  subscribeSheinNoticeChannel,
} from '@/shein-platform/state/sheinNoticeStore';
import { resolveSheinAlertTone } from '@/shein-platform/utils/alertTone';
import '@/styles/shein-platform/shein-notice-drawer.css';

const EMPTY_TEXT = {
  platform: '暂无平台通知。核价、同步、平台推送等相关提醒会显示在这里。',
  system: '暂无系统通知。店铺授权与同步异常等会显示在这里。',
};

function sourceLabel(source) {
  if (source === 'webhook') return fixedUi('平台推送');
  if (source === 'sync') return fixedUi('系统同步');
  if (source === 'business') return fixedUi('业务待办');
  return '';
}

function formatAlertTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * 布局级通知抽屉宿主：
 * - 状态在模块 store，TopBar 重挂载不会被重置关闭
 * - 遮罩禁止被全局 installModalBackdropDismiss 点「关闭」（data-no-backdrop-close）
 * - 列表只读 store，关抽屉不会清空；拉取由 TopBar 负责
 */
export default function SheinNoticeDrawerHost() {
  const channel = useSyncExternalStore(
    subscribeSheinNoticeChannel,
    getSheinNoticeChannel,
    () => null,
  );
  const lockedChannel = useSyncExternalStore(
    subscribeSheinNoticeChannel,
    getSheinNoticeLockedChannel,
    () => 'platform',
  );
  const items = useSyncExternalStore(
    subscribeSheinNoticeAlerts,
    getSheinNoticeAlertItems,
    () => [],
  );

  const open = Boolean(channel);
  const effectiveChannel = channel || lockedChannel || 'platform';
  const title = effectiveChannel === 'system' ? fixedUi('系统通知') : fixedUi('平台通知');

  const [everOpened, setEverOpened] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (open) setEverOpened(true);
  }, [open]);

  // 防御：即便全局 dismiss 漏网，遮罩点击也绝不能冒泡成关闭
  useEffect(() => {
    const node = rootRef.current;
    if (!node || !open) return undefined;
    const block = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      // 只拦截点在遮罩上的事件；抽屉内按钮（帮助/关闭）照常
      if (!target.closest('.spo-notice-backdrop')) return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
    };
    // 必须挂在 document 捕获、且优先于全局 dismiss：用同一阶段更早注册做不到，
    // 因此依赖 data-no-backdrop-close；这里再挡一层 bubble/剩余路径。
    const types = ['pointerdown', 'mousedown', 'mouseup', 'click', 'touchstart', 'touchend'];
    types.forEach((type) => node.addEventListener(type, block, true));
    return () => {
      types.forEach((type) => node.removeEventListener(type, block, true));
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const blockEscape = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener('keydown', blockEscape, true);
    return () => window.removeEventListener('keydown', blockEscape, true);
  }, [open]);

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    closeSheinNotice();
  };

  const handleOpenHelp = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    openSheinPathInNewTab(
      `${SHEIN_NOTICE_HELP_PATH}?tab=notices&channel=${effectiveChannel === 'system' ? 'system' : 'platform'}`,
    );
  };

  if (!everOpened || typeof document === 'undefined') return null;

  const visible = items.filter((item) => {
    if (!item?.id) return false;
    return (item.channel || 'platform') === effectiveChannel;
  });

  return createPortal(
    <div
      ref={rootRef}
      className="spo-notice-root"
      data-open={open ? 'true' : 'false'}
      data-no-backdrop-close=""
      data-testid="shein-notice-drawer-root"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483000,
        display: open ? 'block' : 'none',
        pointerEvents: open ? 'auto' : 'none',
      }}
    >
      {/*
        关键：class 名仍带 -backdrop，会被全局 installModalBackdropDismiss 识别。
        必须显式 data-no-backdrop-close，否则 document 捕获阶段会点「关闭」按钮。
      */}
      <div
        className="spo-notice-backdrop"
        role="presentation"
        aria-hidden="true"
        data-no-backdrop-close=""
        data-backdrop-close="false"
        data-testid="shein-notice-backdrop"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(17, 24, 39, 0.28)',
          pointerEvents: 'auto',
          cursor: 'default',
        }}
      />
      <aside
        className="spo-notice-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid="shein-notice-drawer"
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(360px, 92vw)',
          pointerEvents: 'auto',
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="spo-notice-drawer__head">
          <h2>{title}</h2>
          <div className="spo-notice-drawer__actions">
            <button
              type="button"
              className="spo-btn spo-btn--ghost spo-btn--sm"
              onClick={handleOpenHelp}
            >
              {fixedUi("帮助")}
            </button>
            <button
              type="button"
              className="spo-btn spo-btn--ghost spo-btn--sm"
              data-testid="shein-notice-close"
              onClick={handleClose}
            >
              {fixedUi("关闭")}
            </button>
          </div>
        </div>
        <div className="spo-notice-drawer__body">
          {visible.length === 0 ? (
            <p className="spo-notice-empty">
              {fixedUi(EMPTY_TEXT[effectiveChannel] || EMPTY_TEXT.platform)}
            </p>
          ) : (
            <div className="shein-alerts" data-testid="shein-workspace-alerts">
              {visible.map((item) => {
                const metaSource = sourceLabel(item.source);
                const metaTime = formatAlertTime(item.createdAt);
                const tone = resolveSheinAlertTone(item);
                const isTodo = item.source === 'business';
                return (
                  <div
                    key={item.id}
                    className={`shein-alert shein-alert--${tone}`}
                  >
                    <div className="shein-alert__body">
                      <div className="shein-alert__head">
                        <strong>{item.title}</strong>
                        {isTodo ? (
                          <span className="shein-alert__badge">{fixedUi("业务待办")}</span>
                        ) : null}
                      </div>
                      {!isTodo && (metaSource || metaTime) ? (
                        <div className="shein-alert__meta">
                          {metaSource ? <span>{metaSource}</span> : null}
                          {metaSource && metaTime ? <span aria-hidden="true">·</span> : null}
                          {metaTime ? <span>{metaTime}</span> : null}
                        </div>
                      ) : null}
                      {isTodo && metaTime ? (
                        <div className="shein-alert__meta">
                          <span>{metaTime}</span>
                        </div>
                      ) : null}
                      <p>{item.message}</p>
                      {item.link ? (
                        <Link
                          to={item.link}
                          className="shein-platform__link"
                          onClick={() => closeSheinNotice()}
                        >
                          {isTodo ? fixedUi("去处理") : fixedUi("查看")}
                        </Link>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
}
